import React, { useEffect, useState } from "react";
import { DescriptionText, FlexContainer, H1Text, H2Text, Spinner } from "../../design";
import TransactionModal from "../../components/modal/transaction";
import SuccessModal from "../../components/modal/success";
import { addTransferById, declineTransferLinkByGid, getTransferLinkById, addFundingSourceId } from "../../api";
import { MESSAGES, TRANSFER_LINK_STATUSES, SWIREPAY_URL, TRANSFERLINK_MESSAGES, } from "../../appconstants";
import { AmountDivideByHundred } from "../../helper";
import BaseSplitPage from "../../components/splitpage";
import Header from "./smallscreen/header";
import SmallScreenFooter from "../../components/footer";
import currencyFormatter from "../../utils/currencyformatter";
import TransfersLink from "../../components/transferlink/TransferLink";
import Support from "../../components/support";
import getMenuTabs from "../../utils/getMenuTabs";
import PaymentMethodTabs from "../../components/paymentmethodtabs";
import AlertModal from "../../components/modal/alert";
import WARNING_ICON from "../../assets/imgs/exclamation_warning.svg"
import SUCCESS_ICON from "../../assets/imgs/success.svg";

type Props = {
    history: {
        push: Function,
    },
}

const TransferLink = (props: Props) => {

    const { match: { params: { transferLinkId } }, location: { pathname, search } } = props;
    const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
    const [isTransactionSuccessModalOpen, setTransactionSuccessModalOpen] = useState(false);
    const [transferLink, setTransferLink] = useState(null);
    const [transferType, setTransferType] = useState(null);
    const [issuerType, setIssuerType] = useState(null);
    const [errorMessage, setErrorMessage] = useState();
    const [isShowSpinner, setShowSpinner] = useState(false);
    const [backButtonUrl, setBackButtonUrl] = useState(SWIREPAY_URL);
    const [submitBtnText, setSubmitBtnText] = useState(null);
    const [bankImageUrl, setBankImageUrl] = useState(null);
    const [partnerImageUrl, setPartnerImageUrl] = useState(null);
    const [selectedTab, setSelectedTab] = useState(parseInt(localStorage.getItem(`${transferLinkId}-tap-index`) || 0));
    const [tabName, setTabName] = useState('');

    const defaultAlertConfig = {
        showIcon: false, isOpen: false, hideBtn: true,
        cancel: null, header: null, body: null,
        submitBtnText: null, cancelBtnText: null, showCancelBtn: false,
        showSubmitBtn: false, submit: null, icon: null
    };

    const [alertConfig, setAlertConfig] = useState(defaultAlertConfig);

    useEffect(() => {
        if (search) {
            getTransferLinkDataWithSearchValue();
        } else {
            getTransferLinkDataWithoutSearchValue();
        }
    }, [transferLinkId, search]);

    useEffect(() => {
        if (transferLink && transferLink.currency && transferLink.amount) {
            setSubmitBtnText(`PAY ${currencyFormatter(AmountDivideByHundred(transferLink.amount), transferLink.currency.name)}`)
        }
    }, [transferLink]);

    useEffect(() => {
        setErrorMessage(null);
    }, [selectedTab]);

    const getTransferLinkDataWithSearchValue = async () => {
        const params = new URLSearchParams(search);
        if (!(params.get('ps') || params.get('mp')) || !params.get('secret')) {
            props.history.push(`${pathname}/error`);
            return;
        }
        setShowSpinner(true);
        try {
            const value = await getTransferLinkById(transferLinkId, search);
            if (value) {
                const { redirectUri, status } = value;
                if (TRANSFER_LINK_STATUSES.ACTIVE !== status) {
                    setTransactionSuccessModalOpen(true);
                    setTimeout(function () {
                        const redirectUrl = redirectUri ? redirectUri : SWIREPAY_URL;
                        const urlPath = redirectUrl.includes("?");
                        if (urlPath) {
                            window.location = `${redirectUrl}&sp-transfer-link=${transferLinkId}`;
                        } else if (redirectUrl && !urlPath) {
                            window.location = `${redirectUrl}?sp-transfer-link=${transferLinkId}`;
                        }
                    }, 5000);
                }
            }
        } catch (reason) {
            try {
                const value = await getTransferLinkById(transferLinkId, null);
                if (value) {
                    setTransferLink(value);
                    setIssuerType(value.account && value.account.issuer && value.account.issuer.name);
                    setTransferType(value.transferType && value.transferType.value);
                    setBankImageUrl(value.account && value.account.bankImageUrl);
                    setPartnerImageUrl(value.account && value.account.partnerImageUrl);
                }
            } catch (e) {
                props.history.push(`${pathname}/error`);
            }
            error(reason);
        } finally {
            setShowSpinner(false);
        }
    };

    const error = reason => {
        setErrorMessage(null);
        const { data: { message, messageCode }, status } = reason;
        if (MESSAGES.TRANSFER_LINK.EXPIRED === message) {
            props.history.push(`${pathname}/expired`)
        } else {
            if (messageCode === 5102) {
                props.history.push(`${pathname}/error`);
                return;
            }
            setErrorMessage(message);
            if (status === 500) {
                setErrorMessage(MESSAGES.TRANSFER_LINK.ERROR);
            }
            resetAlertConfig();
            setTransactionModalOpen(false);
            const errorEl = document.getElementById("error");
            if (null !== errorEl) { errorEl.scrollIntoView(); }
        }
    };

    const getTransferLinkDataWithoutSearchValue = async () => {
        setShowSpinner(true);
        try {
            const value = await getTransferLinkById(transferLinkId, null);
            if (value) {
                const { status, redirectUri } = value;
                if (TRANSFER_LINK_STATUSES.ACTIVE !== status) {
                    props.history.push(`${pathname}/expired`);
                    return;
                }
                setTransferLink(value);
                setIssuerType(value.account && value.account.issuer && value.account.issuer.name);
                setTransferType(value.transferType && value.transferType.value);
                setBankImageUrl(value.account && value.account.bankImageUrl);
                setPartnerImageUrl(value.account && value.account.partnerImageUrl);
                setBackButtonUrl(redirectUri || SWIREPAY_URL)
            }
        } catch (e) {
            props.history.push(`${pathname}/error`);
        } finally {
            setShowSpinner(false)
        }
    };

    const getContactGidFromTransfer = () => {
        return transferLink && transferLink.contact && transferLink.contact.gid;
    };

    const chargeByTransferLink = async (data) => {
        const { issuerBank } = data.TransferLinkData;
        issuerBank.countryCode = transferLink.currency.countryAlpha2;
        const fundingSource = {
            contactGid: getContactGidFromTransfer(transferLink),
            issuerBank,
        }
        const payload = {
            contactGid: getContactGidFromTransfer(transferLink), 
            fundingSource 
        }
        try {
            const value = await addFundingSourceId(transferLinkId, payload);
            if (value) {
                const { nextActionUrl, redirectUri } = value;
                if (nextActionUrl) {
                    window.location = nextActionUrl;
                    return;
                }
                setTransactionModalOpen(false);
                setTransactionSuccessModalOpen(true);
                setTimeout(function () {
                    const redirectUrl = redirectUri ? redirectUri : SWIREPAY_URL;
                    const urlPath = redirectUrl.includes("?");
                    if (urlPath) {
                        window.location = `${redirectUrl}&sp-transferLink-link=${transferLinkId}`;
                    } else if (redirectUrl && !urlPath) {
                        window.location = `${redirectUrl}?sp-transferLink-link=${transferLinkId}`;
                    }
                }, 5000);
                if (transferLinkId) { localStorage.removeItem(`${transferLinkId}-tap-index`) }
            }
        } catch (reason) {
            error(reason);
        } 
    };

    const chargeById = async (data) => {
        setTransactionModalOpen(true);
        try {
            const value = await addTransferById(transferLinkId, data);
            if (value) {
                const { nextActionUrl, redirectUri } = value;
                if (nextActionUrl) {
                    window.location = nextActionUrl;
                    return;
                }
                setTransactionModalOpen(false);
                setTransactionSuccessModalOpen(true);
                setTimeout(function () {
                    const redirectUrl = redirectUri ? redirectUri : SWIREPAY_URL;
                    const urlPath = redirectUrl.includes("?");
                    if (urlPath) {
                        window.location = `${redirectUrl}&sp-transferLink-link=${transferLinkId}`;
                    } else if (redirectUrl && !urlPath) {
                        window.location = `${redirectUrl}?sp-transferLink-link=${transferLinkId}`;
                    }
                }, 5000);
                if (transferLinkId) { localStorage.removeItem(`${transferLinkId}-tap-index`) }
            }
        } catch (reason) {
            error(reason);
        }
    };

    const cancelAlertOpen = () => {
        setAlertConfig({
            showIcon: true, isOpen: true, hideBtn: false,
            cancel: resetAlertConfig, header: "Are you sure!", body: "You will not be able to recover this transferLink link",
            submitBtnText: "Yes, decline it!", cancelBtnText: "Cancel", showCancelBtn: true,
            showSubmitBtn: true, submit: declineTransferLink, icon: WARNING_ICON
        });
    };

    const resetAlertConfig = () => {
        setAlertConfig(defaultAlertConfig)
    };

    const declineTransferLink = () => {
        setAlertConfig({
            ...defaultAlertConfig,
            showIcon: true, isOpen: true, hideBtn: true,
            header: TRANSFERLINK_MESSAGES.HEADER, body: TRANSFERLINK_MESSAGES.BODY,
        });
        if (transferLink && transferLink.gid) {
            declineTransferLinkByGid(transferLink.gid).then(value => {
                if (value) {
                    const { redirectUri } = value;
                    setAlertConfig({
                        ...defaultAlertConfig,
                        showIcon: true, isOpen: true, hideBtn: true,
                        header: TRANSFERLINK_MESSAGES.COMPLETE, body: TRANSFERLINK_MESSAGES.REDIRECT,
                        icon: SUCCESS_ICON
                    });
                    setTimeout(function () {
                        const redirectUrl = redirectUri || SWIREPAY_URL;
                        const urlPath = redirectUrl.includes("?");
                        const decline = "decline=true";
                        if (urlPath) {
                            window.location = `${redirectUrl}&${decline}`;
                        } else if (redirectUrl && !urlPath) {
                            window.location = `${redirectUrl}?${decline}`;
                        }
                    }, 5000);
                }
            }).catch(reason => error(reason));
        }
    };
    const uniquePaymentTypes = ['TRANSFER'];
    const tabPanels = [
        {
            children: <TransfersLink
                showCancelBtn={transferLink && transferLink.canDecline}
                cancel={cancelAlertOpen}
                errorMsg={errorMessage}
                errorHeader={null}
                customerReadOnly={false}
                submit={chargeByTransferLink}
                preFillCustomer={transferLink}
                transferType={transferType}
                issuerType={issuerType}
                customerDisabled={true}
                requiredPhone={false}
                headerText={`RECEIVE FUNDS BY ${transferLink && transferLink.transferType && transferLink.transferType.value}`}
                cancelButtonText={"Decline"}
                buttonText={submitBtnText}
                customerValidation={true}
                alpha2={transferLink && transferLink.currency && transferLink.currency.countryAlpha2}
            />
        }
    ];

    useEffect(() => {
        setTabName(uniquePaymentTypes && uniquePaymentTypes[selectedTab]);
    }, [selectedTab, uniquePaymentTypes]);

    return (
        <BaseSplitPage left={
            <>
                <FlexContainer
                    flexDirection="column"
                    position="relative"
                    display="block"
                    height="100%"
                >
                    <div>
                        {
                            (transferLink && transferLink.account && transferLink.account.largeLogo) && (
                                <img src={transferLink.account.largeLogo} style={{ width: "125px" }} alt="" />
                            )
                        }
                        <H1Text>
                            Transfer Link
                        </H1Text>
                        <H2Text>
                            Transfer From
                        </H2Text>
                        <DescriptionText>
                            {(transferLink) && (transferLink.account) && (transferLink.account.name)}
                        </DescriptionText>
                        <H2Text>
                            Transfer Description
                        </H2Text>
                        <DescriptionText>
                            {transferLink && transferLink.description}
                        </DescriptionText>
                        <H2Text>
                            Amount Payable
                        </H2Text>
                        <DescriptionText fontSize="24px">
                            {transferLink ? `${currencyFormatter(AmountDivideByHundred(transferLink.amount), transferLink.currency && transferLink.currency.name)}` : null}
                        </DescriptionText>
                    </div>
                    <div style={{ position: "sticky", top: "85%" }}>
                        <Support email={transferLink && transferLink.support && transferLink.support.email}
                            phone={transferLink && transferLink.support && transferLink.support.phone}
                            partnerImageUrl={partnerImageUrl}
                            bankImageUrl={bankImageUrl}
                            whiteLabel={transferLink && transferLink.account && transferLink.account.whiteLabel}
                             />
                    </div>
                </FlexContainer>
            </>
        } right={
            <>
                <Header 
                    transferLink={transferLink} 
                    bankImageUrl={bankImageUrl} 
                    cancelUrlLink={null}
                />
                <PaymentMethodTabs
                    name={transferLinkId}
                    mode='Transfer'
                    paymentTypes={(transferLink && transferLink.currency && transferLink.currency.countryAlpha2)}
                    tabs={getMenuTabs(uniquePaymentTypes)}
                    tabPanels={tabPanels}
                    defaultSelectedTab={selectedTab || 0}
                    onSelect={data => setSelectedTab(data)}
                    tabName={tabName}
                />
                <SmallScreenFooter
                  partnerImageUrl={partnerImageUrl}
                  bankImageUrl={bankImageUrl}
                  whiteLabel={transferLink && transferLink.account && transferLink.account.whiteLabel}
                />
                <TransactionModal body={null} header={null} isOpen={isTransactionModalOpen} />
                <SuccessModal isOpen={isTransactionSuccessModalOpen} />
                <AlertModal
                    showIcon={alertConfig.showIcon}
                    isOpen={alertConfig.isOpen}
                    hideBtn={alertConfig.hideBtn}
                    cancel={alertConfig.cancel}
                    header={alertConfig.header}
                    body={alertConfig.body}
                    submitBtnText={alertConfig.submitBtnText}
                    cancelBtnText={alertConfig.cancelBtnText}
                    showCancelBtn={alertConfig.showCancelBtn}
                    showSubmitBtn={alertConfig.showSubmitBtn} submit={alertConfig.submit} icon={alertConfig.icon} />
                <Spinner isOpen={isShowSpinner} />
            </>
        } />
    )
};

export default TransferLink;
